import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, FormattedDate, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_CUSTOMER_COMMISSION,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const BASE_RENTAL_CODES = [
  'line-item/hour',
  'line-item/day',
  'line-item/night',
  'line-item/item',
  'line-item/fixed',
];

const sumCents = items =>
  items
    .reduce((acc, item) => acc.plus(item?.lineTotal?.amount || 0), new Decimal(0))
    .toNumber();

/**
 * Computes the provider commission rate (0–1) actually applied to this booking
 * by reading the original line items snapshot. Falls back to env default.
 */
const computeProviderCommissionRate = originalLineItems => {
  const providerCommissionItem = originalLineItems.find(
    i => i.code === LINE_ITEM_PROVIDER_COMMISSION && !i.reversal
  );
  if (!providerCommissionItem) {
    const envPct = parseFloat(process.env.REACT_APP_DEFAULT_PROVIDER_FEE_PERCENT || '3.5');
    return Number.isFinite(envPct) ? envPct / 100 : 0.035;
  }

  const baseRentalItems = originalLineItems.filter(
    i => BASE_RENTAL_CODES.includes(i.code) && !i.reversal
  );
  const baseRentalCents = sumCents(baseRentalItems);
  if (!baseRentalCents) return 0;

  const commissionAbsCents = Math.abs(providerCommissionItem.lineTotal?.amount || 0);
  return commissionAbsCents / baseRentalCents;
};

/**
 * Renders the cancellation refund block beneath the Booking Breakdown totals.
 * Returns null when the transaction has no settlement.cancellation metadata.
 */
const LineItemCancellationRefundMaybe = props => {
  const {
    transaction,
    originalLineItems,
    cancellation,
    isProvider,
    intl,
    marketplaceCurrency,
  } = props;

  if (!cancellation || !originalLineItems || originalLineItems.length === 0) return null;

  const currency =
    originalLineItems[0]?.lineTotal?.currency ||
    transaction?.attributes?.payinTotal?.currency ||
    marketplaceCurrency ||
    'USD';

  const customerItems = originalLineItems.filter(
    i => !i.reversal && (i.includeFor || []).includes('customer')
  );
  const providerItems = originalLineItems.filter(
    i => !i.reversal && (i.includeFor || []).includes('provider')
  );

  const customerOriginalCents = sumCents(customerItems);
  const providerOriginalCents = sumCents(providerItems);

  const totalCents = isProvider ? providerOriginalCents : customerOriginalCents;
  const baseRefundCents = cancellation.baseRefundCents || 0;
  const tierFeeCents = cancellation.tierFeeCents || 0;
  const tier = cancellation.tier;
  const isProviderInitiated = cancellation.cancelledBy === 'provider';
  const showTierFee = isProvider && isProviderInitiated && tierFeeCents > 0;

  const providerCommissionRate = isProvider ? computeProviderCommissionRate(originalLineItems) : 0;
  const refundCents = isProvider
    ? Math.round(baseRefundCents * (1 - providerCommissionRate))
    : baseRefundCents;

  // Provider also loses their payout on settlement-charge refunds. Walk each
  // original snapshot charge and pair it with its live charge to read refund
  // totals; deduct per-charge providerPayoutCents × refund fraction. This is
  // rate-agnostic — providerPayoutCents already reflects whatever commission
  // rate (revenue 96.5%, reimbursement 100%, or any future Push 10 rate)
  // applied at capture time. Refund fraction is capped at the base amount so
  // platform-borne customer-fee refunds (e.g. provider-cancel full refund)
  // don't inflate the provider's loss past 100% of their payout.
  const liveSettlementCharges = Array.isArray(
    transaction?.attributes?.metadata?.settlement?.charges
  )
    ? transaction.attributes.metadata.settlement.charges
    : [];
  const liveChargeById = new Map(liveSettlementCharges.map(c => [c.id, c]));
  const originalSettlementCharges = Array.isArray(cancellation.originalSettlementCharges)
    ? cancellation.originalSettlementCharges
    : [];

  const providerSettlementLossCents = isProvider
    ? originalSettlementCharges.reduce((acc, snap) => {
        const live = liveChargeById.get(snap.id);
        const refundedTotal = ((live && live.refunds) || []).reduce(
          (sum, r) => sum + (r.amountCents != null ? r.amountCents : (r.amount || 0)),
          0
        );
        const baseAmount = snap.amountCents != null ? snap.amountCents : (snap.amount || 0);
        const providerPayout = snap.providerPayoutCents != null ? snap.providerPayoutCents : 0;
        if (baseAmount <= 0 || providerPayout <= 0 || refundedTotal <= 0) return acc;
        const baseRefundedPortion = Math.min(refundedTotal, baseAmount);
        return acc + Math.round(providerPayout * (baseRefundedPortion / baseAmount));
      }, 0)
    : 0;
  const showSettlementLoss = isProvider && providerSettlementLossCents > 0;

  const netCentsRaw = isProvider
    ? totalCents - refundCents - (showTierFee ? tierFeeCents : 0) - providerSettlementLossCents
    : totalCents - refundCents;

  const totalLabelId = isProvider
    ? 'OrderBreakdown.cancellationProviderEarned'
    : 'OrderBreakdown.total';
  const netLabelId = isProvider
    ? showTierFee
      ? 'OrderBreakdown.cancellationAmountOwed'
      : 'OrderBreakdown.cancellationNetEarned'
    : 'OrderBreakdown.cancellationNetPaid';

  const totalMoney = new Money(totalCents, currency);
  const refundMoney = new Money(refundCents, currency);
  const tierMoney = new Money(tierFeeCents, currency);
  const settlementLossMoney = new Money(providerSettlementLossCents, currency);
  const netMoney = new Money(Math.abs(netCentsRaw), currency);

  const formattedDate = (
    <FormattedDate
      value={new Date(cancellation.cancelledAt)}
      year="numeric"
      month="short"
      day="numeric"
    />
  );

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>
          <FormattedMessage id={totalLabelId} />
        </div>
        <div className={css.totalPrice}>{formatMoney(intl, totalMoney)}</div>
      </div>

      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage
            id="OrderBreakdown.cancellationRefundIssuedOn"
            values={{ date: formattedDate }}
          />
        </span>
        <span className={css.itemValue}>{`-${formatMoney(intl, refundMoney)}`}</span>
      </div>

      {showTierFee ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage
              id="OrderBreakdown.cancellationFeeTier"
              values={{ tier }}
            />
          </span>
          <span className={css.itemValue}>{`-${formatMoney(intl, tierMoney)}`}</span>
        </div>
      ) : null}

      {showSettlementLoss ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.cancellationSettlementRefund" />
          </span>
          <span className={css.itemValue}>{`-${formatMoney(intl, settlementLossMoney)}`}</span>
        </div>
      ) : null}

      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>
          <FormattedMessage id={netLabelId} />
        </div>
        <div className={css.totalPrice}>{formatMoney(intl, netMoney)}</div>
      </div>
    </>
  );
};

export default LineItemCancellationRefundMaybe;
